import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, ListTodo } from "lucide-react";
import { toast } from "sonner";
import { PhotographerJobCard, PhotographerJobsEmpty } from "./photographer/PhotographerJobCard";
import { PhotographerWorkloadItem } from "./photographer/PhotographerWorkloadItem";

export default function PhotographerPortal() {
  const { user, logout } = useAuth(); const navigate = useNavigate(); const [jobs, setJobs] = useState([]); const [loading, setLoading] = useState(true);
  const [workload, setWorkload] = useState([]); const [serviceNames, setServiceNames] = useState({});
  const load = async () => { setLoading(true); try { const response = await api.get("/photographer/appointments"); setJobs(response.data); } catch { setJobs([]); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/photographer/workload").then((r) => setWorkload(r.data)).catch(() => setWorkload([]));
    api.get("/services").then((r) => setServiceNames(Object.fromEntries(r.data.services.map((s) => [s.id, s.name])))).catch(() => {});
  }, []);
  const updateStatus = async (id, status) => { try { await api.patch(`/photographer/appointments/${id}/status`, { status }); toast.success("Assignment status updated"); load(); } catch (error) { toast.error(formatError(error.response?.data?.detail)); } };
  const otherWork = workload.filter((item) => !(item.is_primary && jobs.some((job) => job.job_number === item.job_number)));

  return (
    <main className="min-h-screen bg-[#0B0C0E] text-[#F4F4F6]">
      <header className="border-b border-[#282C34] px-5 py-5 sm:px-10 flex items-center justify-between">
        <div><div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#C8B9A6]">Mandalay Media</div><h1 className="font-display text-3xl mt-1">My field schedule</h1></div>
        <div className="flex items-center gap-4">
          <span data-testid="photographer-portal-user" className="hidden sm:inline text-sm text-[#9A9EA7]">{user?.name}</span>
          <Button data-testid="photographer-portal-logout" variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/photographer/login"); }} className="text-[#C8B9A6]"><LogOut size={18} /></Button>
        </div>
      </header>
      <section className="max-w-5xl mx-auto px-5 sm:px-10 py-10">
        <div className="flex items-end justify-between gap-4">
          <div><span className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#C8B9A6]">Upcoming</span><p className="text-sm text-[#9A9EA7] mt-2">Property-local appointment times</p></div>
          <span data-testid="photographer-job-count" className="font-mono text-sm text-[#C8B9A6]">{jobs.length} assigned</span>
        </div>
        {loading ? (
          <div data-testid="photographer-portal-loading" className="py-16 text-[#9A9EA7]">Loading schedule…</div>
        ) : (
          <div className="grid gap-4 mt-7">
            {jobs.map((job) => <PhotographerJobCard key={job.id} job={job} onUpdateStatus={updateStatus} />)}
            {!jobs.length && <PhotographerJobsEmpty />}
          </div>
        )}
        <div className="flex items-end justify-between gap-4 mt-14 pt-10 border-t border-[#282C34]">
          <div><span className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#C8B9A6]"><ListTodo size={13} className="inline mr-1.5 -mt-0.5" />All upcoming assignments</span><p className="text-sm text-[#9A9EA7] mt-2">Includes jobs that don't have a confirmed time yet, and services you're assigned on where someone else is the on-site shooter.</p></div>
          <span data-testid="photographer-workload-count" className="font-mono text-sm text-[#C8B9A6]">{otherWork.length}</span>
        </div>
        <div className="grid gap-3 mt-6">
          {otherWork.map((item) => <PhotographerWorkloadItem key={item.booking_id} item={item} serviceNames={serviceNames} />)}
          {!otherWork.length && <p data-testid="photographer-workload-empty" className="text-sm text-[#9A9EA7]">Nothing else on your plate right now.</p>}
        </div>
      </section>
    </main>
  );
}
